import { useEffect, useCallback, useState } from 'react';
import { toast } from 'react-toastify';
import { useQueryClient } from '@tanstack/react-query';
import { OfflineQueueService } from './OfflineQueueService';
import { createOrder, createTakeawayOrder, updateOrderStatus, addItemsToOrder } from './OrderService';
import { createPayment } from './PaymentService';

export function useOfflineSyncManager() {
  const [isSyncing, setIsSyncing] = useState(false);
  const queryClient = useQueryClient();

  const syncOfflineOperations = useCallback(async () => {
    if (!navigator.onLine) return;

    const queue = await OfflineQueueService.getQueue();
    if (queue.length === 0) return;

    setIsSyncing(true);
    let successCount = 0;

    for (const op of queue) {
      if (op.retries > 3) {
        // Skip operations that failed too many times, to avoid blocking the queue permanently
        console.warn('Skipping stuck operation', op);
        continue;
      }

      try {
        switch (op.type) {
          case 'CREATE_ORDER':
            await createOrder(op.payload);
            break;
          case 'CREATE_TAKEAWAY_ORDER':
            await createTakeawayOrder(op.payload);
            break;
          case 'UPDATE_ORDER_STATUS':
            await updateOrderStatus(op.payload.id, op.payload.status);
            break;
          case 'ADD_ITEMS':
            await addItemsToOrder(op.payload.id, { items: op.payload.items });
            break;
          case 'CREATE_PAYMENT':
            await createPayment(op.payload);
            break;
          default:
            console.warn('Unknown offline operation type', op.type);
        }
        
        await OfflineQueueService.removeOperation(op.id);
        successCount++;
      } catch (err: any) {
        console.error('Offline sync error for op', op.id, err);
        // Only retry if it's a network error. If it's 400 or 500, we probably shouldn't retry infinitely.
        const isNetworkErr = !err.response || err.code === 'ERR_NETWORK';
        if (isNetworkErr) {
          await OfflineQueueService.incrementRetry(op.id);
        } else {
          // If the server rejected it (e.g. invalid status transition), drop it to avoid infinite loops
          await OfflineQueueService.removeOperation(op.id);
        }
      }
    }

    setIsSyncing(false);

    if (successCount > 0) {
      toast.success(`${successCount} comanda(s) sincronizadas con el servidor.`);
      // Invalidate queries so that fresh database records appear everywhere in the app
      queryClient.invalidateQueries({ queryKey: ['tables'] });
      queryClient.invalidateQueries({ queryKey: ['orders'] });
      queryClient.invalidateQueries({ queryKey: ['activeOrder'] });
      queryClient.invalidateQueries({ queryKey: ['ordersByTable'] });
      queryClient.invalidateQueries({ queryKey: ['takeaway'] });
      queryClient.invalidateQueries({ queryKey: ['dashboard'] });
    }
  }, [queryClient]);

  useEffect(() => {
    const handleOnline = () => {
      syncOfflineOperations();
    };

    window.addEventListener('online', handleOnline);

    // Initial check just in case we opened the app while online but had pending operations
    if (navigator.onLine) {
      syncOfflineOperations();
    }

    return () => {
      window.removeEventListener('online', handleOnline);
    };
  }, [syncOfflineOperations]);

  return { syncOfflineOperations, isSyncing };
}
